import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { IconArrowLeft, IconDots, IconFileText, IconHistory, IconReceipt, IconPackage, IconCash } from '@tabler/icons-react';
import { Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Skeleton, StateView, useToast } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from '../finance/access';
import { INVD_BASE, INVD_DOCS, INVD_FLOW, INVD_MAIN, invdCalc, invdLog, invdMoney, invdPays, invdSub, invdTr, type InvDoc, type InvdTab } from './invd';
import s from './invd.module.css';
import './invdetail.i18n';

// Mint-InvoiceDetail-iPad (doc = nhap · phathanh · dagui · dathu · quahan). TIỀN → sau quyền `money`; chỉ đọc, nút ghi báo toast "Chưa mở ghi ở bản này".
const TABS: readonly InvdTab[] = ['lines', 'rel', 'pay', 'log'];
const isDoc = (x: unknown): x is InvDoc => INVD_DOCS.includes(x as InvDoc);

export function InvoiceDetail({ doc: docProp }: { doc?: string }) {
  const t = useT();
  const toast = useToast();
  const access = useFinanceAccess();
  const state = financeScreenState(access);
  const p = useParams({ strict: false }) as { doc?: string };
  const key = docProp ?? p.doc;
  const doc: InvDoc = isDoc(key) ? key : 'nhap';
  const [tab, setTab] = useState<InvdTab>('lines');
  const noWrite = () => toast({ msg: t('ivdt.noWrite') });
  const B = INVD_BASE[doc];
  const c = invdCalc(doc);
  const m = (x: string) => (access.money ? x : '•••••');
  const pct = c.gross > 0 ? Math.min(1, c.paid / c.gross) : 0;
  const [mainK, mainTip] = INVD_MAIN[B.stage]!;
  const pays = invdPays(doc);
  const chipCls = c.over ? s.err : c.paidFull ? s.ok : '';

  return (
    <Page kind="table">
      <PageHeader
        title={c.no}
        lead={<Link to="/ezy/invoice" className={s.back} aria-label={t('ivdt.back')} title={t('ivdt.backTip')}><IconArrowLeft size={18} aria-hidden="true" /></Link>}
        meta={<><span className={`${s.chip} ${chipCls}`} title={invdSub(doc)}><i />{c.status}</span><span className={s.cus}>{B.cus}</span></>}
        tabs={
          <div className={s.tabs} role="tablist" aria-label={t('ivdt.tabsAria')}>
            {TABS.map((k) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{t(`ivdt.tab.${k}`)}</button>)}
          </div>
        }
        actions={
          <>
            <button type="button" className={s.back} aria-haspopup="menu" aria-label={t('ivdt.more')} title={t('ivdt.more')} onClick={() => toast({ msg: t('ivdt.menuSoon') })}><IconDots size={18} aria-hidden="true" /></button>
            <button type="button" className={s.chip} title={mainTip} onClick={noWrite} style={{ background: 'var(--btn)', color: 'var(--btn-tx)', height: 44, padding: '0 18px', borderRadius: 12 }}>{mainK}</button>
          </>
        }
      />
      <StateView state={state} skeleton={<Skeleton rows={9} />}>
        <div className={s.grid}>
          <div className={s.col}>
            {tab === 'lines' && (
              <section className={s.sec} aria-label={t('ivdt.linesSec')}>
                <div className={s.sh}><IconReceipt size={18} aria-hidden="true" />{t('ivdt.linesSec')}</div>
                <div className={`${s.row} ${s.head}`} aria-hidden="true"><span>{t('ivdt.h.code')}</span><span>{t('ivdt.h.desc')}</span><span className={s.r}>{t('ivdt.h.qty')}</span><span className={s.r}>{t('ivdt.h.price')}</span><span className={s.r}>{t('ivdt.h.vat')}</span><span className={s.r}>{t('ivdt.h.amt')}</span></div>
                {c.lines.map((l) => (
                  <div key={l.code} className={s.row}><b>{l.code}</b><span>{l.k}</span><span className={`${s.r} ${s.m}`}>{l.q}</span><span className={`${s.r} ${s.m}`}>{m(invdMoney(l.price))}</span><span className={`${s.r} ${s.l}`}>{l.vat}</span><b className={s.r}>{m(invdMoney(l.value))}</b></div>
                ))}
                <div className={`${s.row} ${s.chrow}`} title={t('ivdt.chTip')}><b>CSHT</b><span>{t('ivdt.chPort')}</span><span className={s.r}>1</span><span className={s.r}>{m(invdMoney(c.ch * 1e6))}</span><span className={s.r}>—</span><b className={s.r}>{m(invdMoney(c.ch * 1e6))}</b></div>
                <div className={s.tot}><span>{t('ivdt.net')}</span><span>{m(invdMoney(c.net * 1e6))}</span><span>{t('ivdt.tax')}</span><span>{m(invdMoney(c.tax * 1e6))}</span><span>{t('ivdt.ch')}</span><span>{m(invdMoney(c.ch * 1e6))}</span><span className={s.big}>{t('ivdt.total')}</span><span className={s.big}>{m(invdMoney(c.gross * 1e6))}</span></div>
              </section>
            )}
            {tab === 'rel' && (
              <>
                <section className={s.sec} aria-label={t('ivdt.jobAria', { job: B.job })}>
                  <div className={s.sh}><IconPackage size={18} aria-hidden="true" /><span style={{ color: 'var(--link)' }}>{B.job}</span></div>
                  <div className={s.pad}><b>{B.pol} → {B.pod}</b></div>
                  <div className={s.job}>
                    <div><small>{t('ivdt.vsl')}</small><b>{B.vsl}</b></div><div><small>ATD</small><b>{B.atd}</b></div><div><small>{t('ivdt.jst')}</small><b>{B.jst}</b></div>
                  </div>
                </section>
                <section className={s.sec} aria-label={t('ivdt.recon')}>
                  <div className={s.sh}><IconFileText size={18} aria-hidden="true" />{t('ivdt.recon')}</div>
                  <div className={s.pad}>
                    <div className={s.rec}><div><small>{t('ivdt.jobFee')}</small><b>{m(invdTr(c.gross))}</b></div><span aria-hidden="true">=</span><div><small>{t('ivdt.onDebit')}</small><b>{m(invdTr(c.gross))}</b></div></div>
                    {B.dem ? <div className={s.alert} title={t('ivdt.demTip')}>{t('ivdt.dem')}</div> : <div className={s.okb}>{t('ivdt.match')}</div>}
                  </div>
                </section>
              </>
            )}
            {tab === 'pay' && (
              pays.length === 0 && !c.isHd ? <section className={s.sec}><div className={s.empty}>{t('ivdt.noPay')}</div></section> : (
                <>
                  <section className={s.sec} aria-label={t('ivdt.prog')}>
                    <div className={s.sh}><IconCash size={18} aria-hidden="true" />{t('ivdt.prog')}</div>
                    <div className={`${s.pad} ${s.ring}`}>
                      <svg role="img" aria-label={t('ivdt.paidPct', { p: Math.round(pct * 100) })} width="160" height="160" viewBox="0 0 132 132">
                        <circle cx="66" cy="66" r="52" fill="none" stroke="var(--line)" strokeWidth="14" />
                        <circle cx="66" cy="66" r="52" fill="none" stroke={c.over ? 'var(--err)' : 'var(--ok)'} strokeWidth="14" strokeLinecap="round" strokeDasharray={`${pct * 326.7} 326.7`} transform="rotate(-90 66 66)" />
                        <text x="66" y="66" textAnchor="middle" fontSize="24" fontWeight="550" fill="var(--tx)">{Math.round(pct * 100)}%</text>
                        <text x="66" y="84" textAnchor="middle" fontSize="12" fill="var(--lb)">{t('ivdt.paidLow')}</text>
                      </svg>
                      <div><div>{t('ivdt.remain')}</div><b>{m(invdTr(c.remain))}</b></div>
                    </div>
                  </section>
                  <section className={s.sec} aria-label={t('ivdt.rcp')}>
                    <div className={s.sh}><IconReceipt size={18} aria-hidden="true" />{t('ivdt.rcp')}</div>
                    <div className={s.pad}>
                      {pays.length === 0 ? <div className={s.empty}>{t('ivdt.noRcp')}</div> : pays.map(([k, d, v]) => <div key={k} className={s.pay}><b>{k}</b><span>{d}</span><span /><b style={{ color: 'var(--tx)' }}>{m(invdTr(v))}</b></div>)}
                    </div>
                  </section>
                </>
              )
            )}
            {tab === 'log' && (
              <section className={s.sec} aria-label={t('ivdt.log')}>
                <div className={s.sh}><IconHistory size={18} aria-hidden="true" />{t('ivdt.log')}</div>
                <div className={s.pad}>
                  {invdLog(doc).map(([k, who, when]) => <div key={k + when} className={s.log}><span className={s.dot} /><div className={s.body}><b>{k}</b><span>{who} · {when}</span></div></div>)}
                </div>
              </section>
            )}
          </div>
          <div className={s.col}>
            <section className={s.sec} aria-label={t('ivdt.sum')}>
              <div className={s.kpis}>
                <div className={s.kpi}><span>{t('ivdt.total')}</span><b>{m(invdTr(c.gross))}</b></div>
                <div className={s.kpi}><span>{t('ivdt.tax')}</span><b>{m(invdTr(c.tax))}</b></div>
                {c.isHd && <div className={s.kpi}><span>{t('ivdt.paid')}</span><b className={c.paidFull ? s.g : ''}>{m(invdTr(c.paid))}</b></div>}
                <div className={s.kpi}><span>{t('ivdt.remain')}</span><b className={c.over ? s.e : ''}>{c.isHd ? m(invdTr(c.remain)) : '—'}</b></div>
                <div className={s.kpi}><span>{c.isHd ? t('ivdt.due') : t('ivdt.dueIssue')}</span><b className={c.over ? s.e : ''}>{c.paidFull ? t('ivdt.paidDone') : c.isHd ? (B.due < 0 ? t('ivdt.overDays', { n: -B.due }) : t('ivdt.days', { n: B.due })) : B.stage === 2 ? t('ivdt.wdays', { n: B.due }) : '—'}</b></div>
              </div>
            </section>
            <section className={s.sec} aria-label={t('ivdt.steps')}>
              <div className={s.sh}>{t('ivdt.steps')}</div>
              <div className={`${s.pad} ${s.flow}`}>
                {INVD_FLOW.map((k, i) => <div key={k} className={`${s.step} ${i < B.stage ? s.done : i === B.stage ? s.cur : ''}`}><i />{k}</div>)}
              </div>
            </section>
            {(c.over || B.lockP) && (
              <div className={`${s.alert} ${c.over ? s.err : ''}`} role="status">
                {c.over ? t('ivdt.overMsg', { n: -B.due, v: m(invdTr(c.remain)) }) : t('ivdt.lockMsg', { per: B.per })}
              </div>
            )}
          </div>
        </div>
      </StateView>
    </Page>
  );
}
